import React, { useEffect, useState } from "react";
import { Mail, Send, Clock, Info, Loader2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function EmailBlastPanel({ user }) {
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const isPremium = user?.role === "premium_creator" || user?.role === "admin";

  const loadHistory = async () => {
    try { const { data } = await api.get("/creator/email-blast/history"); setHistory(data); }
    catch {}
  };
  useEffect(() => { loadHistory(); }, []);

  const send = async () => {
    if (!subject.trim() || !content.trim()) return toast.error("Subject and content required");
    if (!window.confirm(`Send this blast to your opted-in followers?${!isPremium ? " (Regular creators: 1 send per week.)" : ""}`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/creator/email-blast", { subject: subject.trim(), content: content.trim() });
      toast.success(`Sent to ${data.sent} follower${data.sent === 1 ? "" : "s"} (of ${data.opted_in} opted-in)`);
      setSubject(""); setContent("");
      loadHistory();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <section data-testid="email-blast-panel" className="tv-glass rounded-2xl border border-white/10 p-6 max-w-3xl">
      <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-bold flex items-center gap-2">
        <Mail className="w-4 h-4" /> Email blast to followers
      </div>
      <h3 className="font-display font-black text-2xl text-white tracking-tight mt-1">Reach your audience directly</h3>
      <p className="text-white/60 text-sm mt-2">
        We'll deliver to every follower who opted into marketing emails. {isPremium ? (
          <span className="text-emerald-300 font-semibold">Premium creators: unlimited sends.</span>
        ) : (
          <span className="text-yellow-300 font-semibold">Regular creators: 1 blast per week.</span>
        )}
      </p>

      <div className="mt-5 space-y-3">
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Subject</span>
          <input
            data-testid="blast-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={160}
            placeholder="New drop tonight at 8PM ET"
            className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm outline-none focus:border-tv-accent"
          />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Message</span>
          <textarea
            data-testid="blast-content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            maxLength={4000}
            rows={8}
            placeholder="Hey fam, I just dropped a new track…"
            className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm outline-none focus:border-tv-accent resize-none"
          />
          <div className="text-[10px] text-white/40 mt-1">{content.length}/4000</div>
        </label>
      </div>

      <div className="mt-4 flex gap-2 items-start text-xs text-white/60 bg-white/[0.02] border border-white/10 rounded-lg px-3 py-2">
        <Info className="w-4 h-4 shrink-0 mt-0.5 text-tv-accent" />
        <div>Only followers who explicitly opted into marketing emails in their notification settings will receive this. The same blast will also appear in their notification bell.</div>
      </div>

      <button
        data-testid="blast-send-btn"
        onClick={send}
        disabled={busy || !subject.trim() || !content.trim()}
        className="mt-5 bg-tv-accent hover:bg-yellow-400 disabled:opacity-50 text-black font-bold px-6 py-3 rounded-full flex items-center gap-2 text-sm"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        {busy ? "Sending…" : "Send blast"}
      </button>

      {history.length > 0 && (
        <div className="mt-8">
          <div className="text-xs uppercase tracking-wider text-white/50 font-bold mb-2 flex items-center gap-2"><Clock className="w-3 h-3" /> Recent blasts</div>
          <div className="space-y-2" data-testid="blast-history">
            {history.map((h) => (
              <div key={h.id} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5">
                <div className="flex-1 min-w-0">
                  <div className="text-white font-semibold text-sm truncate">{h.subject}</div>
                  <div className="text-[11px] text-white/50">{new Date(h.sent_at).toLocaleString()} · {h.recipients_count} delivered</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
